// api/roboneo.ts
import { createApi } from '@meitu/catalyze';
import { useOfficialStore } from '$/store/useOfficialStore';
import { AxiosError, AxiosRequestConfig } from 'axios';
import APP from './api';
import {
  addCommonHeaders,
  addCommonParams,
  type RoboneoRequestConfig,
} from './helpers';
// import { createErrorMiddleware } from './middlewares/errorMiddleware';
import { createLoggingMiddleware } from './middlewares/loggingMiddleware';
import { createRequestErrorMiddleware } from './middlewares/createRequestErrorMiddleware';

export interface RequestMiddleware {
  onRequest?: (config: AxiosRequestConfig) => AxiosRequestConfig | Promise<AxiosRequestConfig>;
  onRequestError?: (error: AxiosError) => any | Promise<any>;

}

export interface RequestOptions extends RoboneoRequestConfig<any> {
  /**
   * 是否使用内网地址
   */
  isInternal?: boolean;
}

export interface RoboneoApiOptions {
  middlewareConfig?: { errorMiddleware?: any;logging: boolean; commonParamsHeadersMiddleware?: RequestMiddleware; };
  canvasMiddlewareConfig?: { errorMiddleware?: any;logging: boolean; commonParamsHeadersMiddleware?: RequestMiddleware; };
  agentSSERequestConfig?: {
    extraParams?: Record<string, any>;
    extraHeaders?: Record<string, any>;
  };
  /**
   * Agent 场景：生成媒体插入 Infinite Canvas 后是否调用资产库 createAsset。
   * 默认 true（未配置时由 MediaInsertManager 视为开启）；显式传 false 可关闭入库。
   */
  agentCanvasMediaConfig?: {
    createAssetAfterInsert?: boolean;
  };
  /**
   * AgentTeam 接口配置，宿主可传入自定义 baseUrl 和通用参数/请求头。
   * 不传则使用项目默认的环境配置。
   */
  agentTeamApiConfig?: {
    baseUrl?: string;
    extraParams?: Record<string, any>;
    extraHeaders?: Record<string, any>;
  };
}
let requestApi: ReturnType<typeof createApi> | undefined;
const createRoboneoApi = (options: RoboneoApiOptions) => {
  const { errorMiddleware, logging = true, commonParamsHeadersMiddleware = {} } = options.middlewareConfig || {};
  requestApi = createApi({
    baseURL: APP.roboneo.baseURL(),
    withCredentials: true,
    timeout: 30000,
    transformResponse: [
      (data: any) => {
        if (data && data instanceof ArrayBuffer) {
          return data;
        }
        return data;
      },
    ],
  });

  requestApi.modifyApiInstance((api) => {
    // 添加公共参数和头部的中间件
    api.interceptors.request.use(
      async (config) => {
        addCommonParams(config);
        await addCommonHeaders(config);
        return config;
      },
      (error) => Promise.reject(error),
    );
    // 构建中间件数组
    const middlewares: any[] = [createRequestErrorMiddleware()];

    // 根据配置添加中间件
    if (logging !== false) {
      const { env } = useOfficialStore.getState();
      middlewares.push(createLoggingMiddleware(env === 'development'));
    }
    if (Object.keys(commonParamsHeadersMiddleware).length) {
      middlewares.push(commonParamsHeadersMiddleware);
    }
    middlewares.push(errorMiddleware?.());

    // if (options.middlewareConfig?.error !== false) {
    //   middlewares.push(createErrorMiddleware(options.navigate));
    // }
    // 添加自定义中间件  //待定，看外部需不需要
    // if (options.middlewareConfig?.customMiddlewares) {
    //   middlewares.push(...options.middlewareConfig.customMiddlewares);
    // }

    api.interceptors.request.use(
      (config) => {
        const newConfig = { ...config };
        // 将path_scene参数放到url后面拼接，方便调试
        if (config.data?.parameter?.path_scene) {
          newConfig.url = `${newConfig.url}/${config.data.parameter.path_scene}`;
        }
        return middlewares.reduce((cfg, middleware) => {
          return middleware.onRequest ? middleware.onRequest(cfg) || cfg : cfg;
        }, newConfig);
      },
      (error) => {
        middlewares.forEach((mw) => mw?.onError && mw?.onError(error));
        return Promise.reject(error);
      },
    );

    api.interceptors.response.use(
      (response) => {
        return middlewares.reduce((resp, middleware) => {
          return middleware.onResponse ? middleware.onResponse(resp) || resp : resp;
        }, response);
      },
      (error) => {
        middlewares.forEach((mw) => mw.onError && mw.onError(error));
        return Promise.reject(error);
      },
    );
  });
};
const roboneoApi = {
  request: (options: RequestOptions) => {
    if (!requestApi) {
      throw Error('未初始化');
    }
    if (options.isInternal) {
      // eslint-disable-next-line no-param-reassign
      options.baseURL = APP.roboneo.baseInternalURL();
    } else {
      // eslint-disable-next-line no-param-reassign
      options.baseURL = APP.roboneo.baseURL();
    }
    return requestApi.request(options);
  },
};
// 导出创建方法而不是直接导出实例
export { createRoboneoApi, roboneoApi };
